import z from "zod";
import { type StrapiFormComponent } from "~/services/cms/models/formElements/StrapiFormComponent";
import { hiddenInputZodDescription } from "~/services/validation/hiddenInput";
import { ibanZodDescription } from "~/services/validation/iban";
import { filesUploadZodDescription } from "~/services/validation/pdfFileSchema";
import { phoneNumberZodDescription } from "~/services/validation/phoneNumber";
import FilesUpload from "../inputs/filesUpload/FilesUpload";
import { mapLookValue } from "~/components/content/ContentComponents";
import HiddenInput from "../inputs/hidden/HiddenInput";
import IbanInput from "../inputs/iban/IbanInput";
import TelephoneInput from "../inputs/telephone/TelephoneInput";
import { type ControlledFieldConfig } from "~/domains/pageSchemas";
import { numberIncrementZodDescription } from "~/services/validation/numberIncrement";
import NumberIncrement from "../inputs/number/NumberIncrement";
import {
  dynamicSelectZodDescription,
  type DynamicOptions,
} from "~/services/validation/dynamicSelect";
import Select from "../inputs/select/Select";
import {
  autoSuggestZodDescription,
  type DataListType,
} from "~/services/validation/autoSuggest";
import { type StrapiAutoSuggestComponent } from "~/services/cms/models/formElements/StrapiAutoSuggestInput";
import AutoSuggestInput from "~/components/formElements/inputs/autoSuggest/AutoSuggestInput";
import { useFlowLoaderDataContext } from "~/components/hooks/useFlowLoaderDataContext";
import { getDataListArgumentToAutoSuggestionInput } from "./getDataListArgumentToAutoSuggestionInput";
import { reuseBeweisZodDescription } from "~/services/validation/reuseBeweis";
import { ReuseBeweisCheckbox } from "~/domains/geldEinklagen/formular/klage-erstellen/begruendung/components/ReuseBeweisCheckbox";
import {
  getDocumentsToBeReusedFromOtherAbschnitte,
  getPersonenToBeReusedFromOtherAbschnitte,
} from "~/domains/geldEinklagen/formular/klage-erstellen/begruendung/components/reuseBeweise";
import { type GeldEinklagenFormularUserData } from "~/domains/geldEinklagen/formular/userData";
import { klageParteiCheckboxesZodDescription } from "~/services/validation/klageParteiCheckboxes";
import { KlageParteiCheckboxes } from "~/domains/geldEinklagen/anwaltschaft/hauptUndNebenantraege/components/KlageParteiCheckboxes";
import { annahmeverzugCheckboxZodDescription } from "~/services/validation/annahmeverzugCheckbox";
import type { GeldEinklagenAnwaltschaftUserData } from "~/domains/geldEinklagen/anwaltschaft/userData";
import { AnnahmeverzugCheckbox } from "~/domains/geldEinklagen/anwaltschaft/hauptUndNebenantraege/components/AnnahmeverzugCheckbox";

const specialComponentDescriptions = [
  filesUploadZodDescription,
  hiddenInputZodDescription,
  ibanZodDescription,
  phoneNumberZodDescription,
  numberIncrementZodDescription,
  dynamicSelectZodDescription,
  autoSuggestZodDescription,
  reuseBeweisZodDescription,
  klageParteiCheckboxesZodDescription,
  annahmeverzugCheckboxZodDescription,
] as const;

type SpecialComponentDescription =
  (typeof specialComponentDescriptions)[number];

export const extractZodDescription = (schema: z.ZodType) => {
  let nestedDescription: string | undefined;
  if (schema instanceof z.ZodUnion) {
    nestedDescription = schema.options
      .map((innerSchema) => (innerSchema as z.ZodType).description)
      .find(Boolean);
  }
  if (schema instanceof z.ZodPipe) {
    nestedDescription =
      (schema.out as z.ZodType).description ??
      (schema.in as z.ZodType).description;
  }
  return schema.description ?? nestedDescription;
};

const getBeweisType = (fieldSchema: z.ZodType, fieldName: string) => {
  let beweisType = fieldSchema.meta()?.beweisType;

  if (fieldSchema instanceof z.ZodUnion && !beweisType) {
    beweisType = fieldSchema.options
      .map((innerSchema) => (innerSchema as z.ZodType).meta()?.beweisType)
      .find(Boolean);
  }

  if (!beweisType) {
    throw new Error(
      `ReuseBeweisCheckbox field ${fieldName} is missing a beweisType in the Zod schema.`,
    );
  }

  return beweisType;
};

export const isSpecialComponentDescriptions = (
  val?: any,
): val is SpecialComponentDescription =>
  specialComponentDescriptions.includes(val);

export const renderSpecialMetaDescriptions = (
  fieldName: string,
  description: SpecialComponentDescription,
  fieldSchema: z.ZodType,
  controlledFieldConfig?: ControlledFieldConfig,
  matchingElement?: StrapiFormComponent,
  dynamicOptions?: DynamicOptions,
) => {
  const { userData } = useFlowLoaderDataContext();

  const errorMessages =
    matchingElement && "errorMessages" in matchingElement
      ? matchingElement.errorMessages
      : undefined;

  switch (description) {
    case filesUploadZodDescription: {
      const {
        fileUploadDescription,
        fileUploadInlineNotices,
        fileUploadTitle,
      } = getFileUploadContent(matchingElement);

      return (
        <FilesUpload
          key={fieldName}
          name={fieldName}
          title={fileUploadTitle}
          description={fileUploadDescription}
          inlineNotices={fileUploadInlineNotices?.map((inlineNotice) => ({
            ...inlineNotice,
            look: mapLookValue(inlineNotice.look),
          }))}
          errorMessages={errorMessages}
        />
      );
    }

    case hiddenInputZodDescription: {
      return <HiddenInput key={fieldName} name={fieldName} />;
    }

    case ibanZodDescription: {
      return (
        <IbanInput
          key={fieldName}
          name={fieldName}
          controlledFieldConfig={controlledFieldConfig}
          {...matchingElement}
        />
      );
    }

    case phoneNumberZodDescription: {
      return (
        <TelephoneInput key={fieldName} name={fieldName} {...matchingElement} />
      );
    }

    case numberIncrementZodDescription: {
      const { minValue, maxValue } = getMinMaxValueByFieldSchema(fieldSchema);

      return (
        <NumberIncrement
          key={fieldName}
          name={fieldName}
          min={minValue}
          max={maxValue}
          {...matchingElement}
        />
      );
    }

    // MVP dynamic select implementation. Used currently only in Nachlass Erbfolge
    case dynamicSelectZodDescription: {
      const options = dynamicOptions?.[fieldName] ?? [];
      const label =
        matchingElement && "label" in matchingElement
          ? matchingElement.label
          : undefined;

      return (
        <Select
          key={fieldName}
          name={fieldName}
          options={options}
          label={label}
          errorMessages={errorMessages}
        />
      );
    }

    case autoSuggestZodDescription: {
      const autoSuggestElement = matchingElement as StrapiAutoSuggestComponent;
      const dataListType = fieldSchema.meta()?.type as DataListType;

      if (!dataListType) {
        throw new Error(
          `AutoSuggestInput field ${fieldName} is missing a dataList type in the Zod schema.`,
        );
      }

      const dataListArgument = getDataListArgumentToAutoSuggestionInput(
        fieldSchema,
        userData,
      );

      return (
        <AutoSuggestInput
          {...autoSuggestElement}
          name={fieldName}
          dataList={dataListType}
          dataListArgument={dataListArgument}
          key={fieldName}
        />
      );
    }
    case reuseBeweisZodDescription: {
      const beweisType = getBeweisType(fieldSchema, fieldName);

      const abschnitte =
        (userData as GeldEinklagenFormularUserData).abschnitte ?? [];
      const arrayIndexes = (userData as GeldEinklagenFormularUserData).pageData
        ?.arrayIndexes;
      const itemIndexAbschnitte = arrayIndexes?.[0] ?? 0;

      const beweisOptions =
        beweisType === "document"
          ? getDocumentsToBeReusedFromOtherAbschnitte(
              abschnitte,
              itemIndexAbschnitte,
            )
          : getPersonenToBeReusedFromOtherAbschnitte(
              abschnitte,
              itemIndexAbschnitte,
            );

      return (
        <ReuseBeweisCheckbox
          key={fieldName}
          name={fieldName}
          options={beweisOptions}
        />
      );
    }
    case klageParteiCheckboxesZodDescription: {
      const geldEinklagenUserData =
        userData as GeldEinklagenFormularUserData & {
          klagenden?: React.ComponentProps<
            typeof KlageParteiCheckboxes
          >["parties"];
          beklagten?: React.ComponentProps<
            typeof KlageParteiCheckboxes
          >["parties"];
        };
      const isBeklagte = fieldName.endsWith("#beklagte");

      const parties = isBeklagte
        ? (geldEinklagenUserData.beklagten ?? [])
        : (geldEinklagenUserData.klagenden ?? []);

      return (
        <KlageParteiCheckboxes
          key={fieldName}
          name={fieldName}
          parties={parties}
          relationship={isBeklagte ? "beklagte" : "klagende"}
        />
      );
    }
    case annahmeverzugCheckboxZodDescription: {
      const geldEinklagenUserData =
        userData as GeldEinklagenFormularUserData & {
          beklagten?: React.ComponentProps<
            typeof AnnahmeverzugCheckbox
          >["parties"];
          hauptantrag?: GeldEinklagenAnwaltschaftUserData["hauptantrag"];
        };

      const arrayIndexes = geldEinklagenUserData.pageData?.arrayIndexes;
      const hauptantragIndex = arrayIndexes?.[0] ?? 0;
      const hauptantrag = geldEinklagenUserData.hauptantrag?.[hauptantragIndex];

      return (
        <AnnahmeverzugCheckbox
          key={fieldName}
          name={fieldName}
          parties={geldEinklagenUserData.beklagten ?? []}
          selectedBeklagte={hauptantrag?.beklagte}
          zahlungsantragIndex={hauptantragIndex}
        />
      );
    }
  }
};

function getMinMaxValueByFieldSchema(fieldSchema: z.ZodType) {
  if (!(fieldSchema instanceof z.ZodNumber)) {
    return { minValue: undefined, maxValue: undefined };
  }

  const minValue = fieldSchema.minValue ?? undefined;
  const maxValue = fieldSchema.maxValue ?? undefined;
  return { minValue, maxValue };
}

const getFileUploadContent = (matchingElement?: StrapiFormComponent) => {
  const fileUploadTitle =
    matchingElement && "title" in matchingElement
      ? matchingElement.title
      : undefined;
  const fileUploadDescription =
    matchingElement && "description" in matchingElement
      ? matchingElement.description
      : undefined;

  const fileUploadInlineNotices =
    matchingElement && "inlineNotices" in matchingElement
      ? matchingElement.inlineNotices
      : undefined;

  return { fileUploadTitle, fileUploadDescription, fileUploadInlineNotices };
};
